import { parseISO } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";


export const areArraysIdentical = (array1, array2) => {
  const array2Sorted = array2.slice().sort();
  return array1.length === array2.length && array1.slice().sort().every(function (value, index) {
    if (typeof value === "string" || !isNaN(value))
      return value === array2Sorted[index];
    else
      return JSON.stringify(value) == JSON.stringify(array2Sorted[index]);
  });
}


export const capitalize = (s) => {
  if (typeof s !== 'string') return ''
  return s.charAt(0).toUpperCase() + s.slice(1)
}


export const keyToLabel = (key) => {
  if (!isNaN(key))
    return key;
  let label_array =
    key
      .replace(/__.*__/, "")
      .replaceAll("_", " ")
      .toLowerCase()
      .replaceAll("ae", "ä")
      .replaceAll("oe", "ö")
      .replaceAll("ue", "ü")
      .split(" ");
  label_array.forEach((word, i) => {
    if (["iban", "bic", "oö", "nö", "stmk", "bgld", "ktn", "sbg", "pkw", "plz"].includes(word))
      label_array[i] = word.toUpperCase();
    else if (!["der", "die", "das", "in", "im", "des", "für", "von", "davon", "und", "mit", "ohne", "oder", "nicht", "bis", "diese", "nach"].includes(word))
      label_array[i] = capitalize(word);
  })
  return label_array.join(" ");
}

/**
 * Convert a database date 2020-01-29T16:04:37 or a date object to human readable date 29.1.2020
 * @param {String|Date} date
 */
export const formatDate = (date = null, toLocalTimeZone = false) => {
  let dateobject = date
    ? date instanceof Date
      ? date
      : parseISO(date)
    : new Date();

  if (toLocalTimeZone)
    dateobject = toZonedTime(fromZonedTime(dateobject, "UTC"), Intl.DateTimeFormat().resolvedOptions().timeZone);


  return (
    dateobject.getDate() +
    "." +
    (dateobject.getMonth() + 1) +
    "." +
    dateobject.getFullYear()
  );
};

/**
 * Convert a database date 2020-01-29T16:04:37 or a date object to human readable datetim 29.1.2020 16:04
 * @param {String|Date} date
 */
export const formatDateTime = (date = null, toLocalTimeZone = false) => {
  let dateobject = date
    ? date instanceof Date
      ? date
      : parseISO(date)
    : new Date();
  if (toLocalTimeZone)
    dateobject = toZonedTime(fromZonedTime(dateobject, "UTC"), Intl.DateTimeFormat().resolvedOptions().timeZone);

  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'full', timeStyle: 'short' }).format(dateobject);
};


export const isValidDateObject = (value) => {
  // https://masteringjs.io/tutorials/fundamentals/typeof-date
  return value instanceof Date && !isNaN(value);
};

export const formatCurrency = (number, decimals = 2) => {
  if (typeof number !== "number")
    return "--";
  return number.toLocaleString("de-DE", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Round numbers to a certain amount of decimals
 * @param {Float} number
 * @param {Int} decimals
 */
export const roundTo = (number, decimals) => {
  const factor = Math.pow(10, decimals);
  number = parseFloat(number);
  return Math.round((number + Number.EPSILON) * factor) / factor;
};



export const getMonthsBetween = (date1, date2, roundUpFractionalMonths) => {
  //Months will be calculated between start and end dates.
  //Make sure start date is less than end date.
  //But remember if the difference should be negative.
  var startDate = date1;
  var endDate = date2;
  var inverse = false;
  if (date1 > date2) {
    startDate = date2;
    endDate = date1;
    inverse = true;
  }

  //Calculate the differences between the start and end dates
  var yearsDifference = endDate.getFullYear() - startDate.getFullYear();
  var monthsDifference = endDate.getMonth() - startDate.getMonth();
  var daysDifference = endDate.getDate() - startDate.getDate();

  var monthCorrection = 0;
  //If roundUpFractionalMonths is true, check if an extra month needs to be added from rounding up.
  //The difference is done by ceiling (round up), e.g. 3 months and 1 day will be 4 months.
  if (roundUpFractionalMonths === true && daysDifference > 0) {
    monthCorrection = 1;
  }
  //If the day difference between the 2 months is negative, the last month is not a whole month.
  else if (roundUpFractionalMonths !== true && daysDifference < 0) {
    monthCorrection = -1;
  }

  return (inverse ? -1 : 1) * (yearsDifference * 12 + monthsDifference + monthCorrection);
};



export const convertBytesToMbsOrKbs = (filesize) => {
  var size = ""; // I know, not technically correct...

  if (filesize >= 1000000) {
    size = formatCurrency(filesize / 1000000, 1) + " MB";
  } else if (filesize >= 1000) {
    size = Math.round(filesize / 1000) + " KB";
  } else {
    size = Math.round(filesize) + " B";
  }

  return size;
};


export const stringToFilename = (str) => {
  if (!str)
    return "";
  str = str.replace(/^\s+|\s+$/g, ''); // trim
  str = str.toLowerCase();

  // remove accents, swap ñ for n, etc
  const replacements = {
    "à": "a",
    "á": "a",
    "â": "a",
    "ä": "ae",
    "è": "e",
    "é": "e",
    "ë": "e",
    "ê": "e",
    "ì": "i",
    "í": "i",
    "ï": "i",
    "î": "i",
    "ò": "o",
    "ó": "o",
    "ô": "o",
    "ö": "oe",
    "ù": "u",
    "ú": "u",
    "û": "u",
    "ü": "ue",
    "ß": "ss",
  }
  Object.keys(replacements).forEach(search => {
    str = str.replace(new RegExp(search, 'g'), replacements[search]);
  })

  str = str.replaceAll('.', '_') // replace a dot by a dash 
    .replaceAll(',', '_')
    .replaceAll('-', '_')
    .replace(/[^a-z0-9 _]/g, '') // remove invalid chars
    .replace(/\s+/g, '_') // collapse whitespace and replace by a dash
    .replace(/_+/g, '_') // collapse dashes
    .replace(/\//g, ''); // collapse all forward-slashes

  return str;
}


export const getMetafieldValue = (metafield) => {
  if (!metafield)
    return null;
  if (["STRING_ENUM", "STRING", "DATE"].includes(metafield.field_type))
    return metafield.meta_value;
  if (["INT_ENUM", "INT"].includes(metafield.field_type))
    return metafield.meta_value_numeric;
  if (["BOOLEAN"].includes(metafield.field_type))
    return metafield.meta_value_boolean;
}